import { useState, useEffect, ReactNode } from 'react';
import Head from 'next/head';
import Topbar from '@common/layout/Topbar';
import Box from '@mui/material/Box';
import { useTheme, Button, Typography, Fade, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useAuth from '@modules/auth/hooks/api/useAuth';
import LoadingScreen from '@common/layout/LoadingScreen';
import { useRouter } from 'next/router';
import ApiRoutes from '@common/defs/api-routes';
import {
  CONTENT_PADDING_X,
  CONTENT_PADDING_Y,
  MAX_CONTENT_WIDTH,
  BOTTOMBAR_HEIGHT,
} from '@common/defs/layout';
import BottomBar from '@common/layout/BottomBar';
import OnboardingWorkflow from '@modules/account/components/onboarding/OnboardingWorkflow';
import DiscordButton from '@common/layout/DiscordButton';
import useUsers from '@modules/users/hooks/api/useUsers';
import WaitlistBanner from '@common/layout/WaitlistBanner';
import WebsiteBanner from '@common/layout/WebsiteBanner';
import MobileTopbar from '@common/layout/MobileTopbar';
import AdminButton from '@common/layout/AdminButton';
import UpgradeButton from '@common/layout/UpgradeButton';
import FormationButton from '@common/layout/FormationButton';
import ChatButton from '@common/layout/ChatButton';
import AnnouncementPopup from '@modules/announcements/components/AnnouncementPopup';

interface ILayoutProps {
  children: ReactNode;
}

const Layout = (props: ILayoutProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const router = useRouter();
  const underMaintenance = process.env.NEXT_PUBLIC_UNDER_MAINTENANCE === 'true';
  const [display, setDisplay] = useState(true);
  const { children } = props;
  const { user, initialized } = useAuth();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const { waitlistData } = useUsers();
  const shouldHideNavigation = () => {
    const pathname = router.pathname;
    const authPagesWithoutNav = [
      ApiRoutes.Auth.Social,
      ApiRoutes.Auth.CompleteRegistration,
      ApiRoutes.Auth.VerifyEmail,
    ];
    if (authPagesWithoutNav.some((page) => pathname.startsWith(page))) {
      return true;
    }
    if (user && user.registrationStep && user.registrationStep !== 'completed') {
      return true;
    }
    return false;
  };

  const hideNav = shouldHideNavigation();

  useEffect(() => {
    setDisplay(!underMaintenance);
  }, [underMaintenance]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingTimeout(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (!display) {
    return (
      <Box
        id="webview-container"
        sx={{
          height: '100vh',
          backgroundColor: 'common.white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box sx={{ padding: 1 }}>
          <Typography textAlign="center" marginBottom={2}>
            Notre application est en maintenance pour environ 1 heure afin d'améliorer ses
            performances.
          </Typography>
          <Typography textAlign="center" marginBottom={2}>
            Merci de votre compréhension.
          </Typography>
          <Button
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 55,
              fontWeight: 500,
              borderRadius: '4px',
              fontFamily: 'Raleway',
              backgroundColor: '#ff7b00',
              color: 'white',
              fontSize: 16,
              gap: '8px',
              marginTop: '24px',
              marginLeft: 'auto',
              marginRight: 'auto',
              paddingLeft: '20px',
              paddingRight: '20px',
            }}
            onClick={() => {
              window.history.back();
            }}
          >
            <ArrowBackIcon />
            Retour
          </Button>
        </Box>
      </Box>
    );
  }

  if (!initialized && !loadingTimeout) {
    return (
      <div>
        <Head>
          <title>{process.env.NEXT_PUBLIC_APP_TITLE || 'AlloClients'}</title>
          <meta name="viewport" content="initial-scale=1, width=device-width" />
        </Head>
        <LoadingScreen />
      </div>
    );
  }

  if (!user || hideNav) {
    const isFullWidthPage =
      ['/pro', '/systemx'].includes(router.pathname) || router.pathname.startsWith('/aide');
    return (
      <div>
        <Head>
          <title>{process.env.NEXT_PUBLIC_APP_TITLE || 'AlloClients'}</title>
          <meta name="viewport" content="initial-scale=1, width=device-width" />
        </Head>
        {isFullWidthPage ? (
          <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
            {children}
          </Box>
        ) : (
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.background.default,
            }}
          >
            {children}
          </Box>
        )}
      </div>
    );
  }

  return (
    <div>
      <Head>
        <title>{process.env.NEXT_PUBLIC_APP_TITLE || 'AlloClients'}</title>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
      </Head>
      <Box
        sx={{
          display: 'flex',
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
          flexDirection: 'column',
        }}
      >
        {process.env.NEXT_PUBLIC_SHOW_WAITLIST !== 'false' &&
          waitlistData &&
          waitlistData.isInWaitlist && <WaitlistBanner />}
        <WebsiteBanner />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          {!isMobile && <Topbar />}
          <Box
            component="main"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              paddingX: CONTENT_PADDING_X,
              paddingTop: isMobile
                ? CONTENT_PADDING_Y
                : {
                    md: `calc( ${theme.spacing(CONTENT_PADDING_Y.md)})`,
                  },
              paddingBottom: isMobile
                ? {
                    xs: `calc(${BOTTOMBAR_HEIGHT}px + ${theme.spacing(CONTENT_PADDING_Y.xs)} 
                    + 50px)`,
                    md: `calc(${BOTTOMBAR_HEIGHT}px + ${theme.spacing(CONTENT_PADDING_Y.md)})`,
                  }
                : CONTENT_PADDING_Y,
              maxWidth: MAX_CONTENT_WIDTH + 'px',
              width: '100%',
            }}
          >
            {isMobile && <MobileTopbar />}
            <OnboardingWorkflow>
              <Fade in timeout={300}>
                <Box>{children}</Box>
              </Fade>
            </OnboardingWorkflow>
          </Box>
          {isMobile && <BottomBar />}
        </Box>
      </Box>
      {user?.isOnboardingCompleted && <AnnouncementPopup />}
      <Box
        sx={{
          position: 'fixed',
          bottom: (isMobile ? BOTTOMBAR_HEIGHT : 0) + 30 + 'px',
          right: 15,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box sx={{}}>
          <AdminButton size={isMobile ? 40 : 50} />
        </Box>
        <Box>
          <FormationButton size={isMobile ? 40 : 50} />
        </Box>
        {isMobile && (
          <Box>
            <UpgradeButton size={40} />
          </Box>
        )}
        {/* <Box sx={{}}>
          <FeedbackButton size={isMobile ? 40 : 50} />
        </Box> */}
        <Box sx={{}}>
          <DiscordButton size={isMobile ? 40 : 50} />
        </Box>
        {/* <Box sx={{}}>
          <ChatButton size={isMobile ? 40 : 50} />
        </Box> */}
      </Box>
    </div>
  );
};

export default Layout;
